import { ButtonSeverity } from './types/buttonServerity';

interface Props {
  severity: string;
  link: boolean;
  plain: boolean;
  outlined: boolean;
  text: boolean;
}

interface Context {
  disabled: boolean;
}

interface RootArguments {
  props: Props;
  context: Context;
}

export const button = {
  root: ({
    props,
    context,
  }: RootArguments): {
    class: Array<Record<string, boolean> | string>;
  } => ({
    class: [
      'min-w-36 flex font-serif font-bold justify-center items-center px-4 py-4 rounded-md',
      { 'bg-accent': props.severity === null },

      { 'bg-positive': props.severity === ButtonSeverity.success },

      { 'bg-primaryGold': props.severity === ButtonSeverity.primary },
      {
        'focus:ring-golden focus:bg-primaryGold/70  hover:ring hover:ring-golden ': props.severity === 'primary',
      },

      { 'bg-neutral-400': props.severity === ButtonSeverity.ghost },
      {
        'focus:ring-neutral-300 focus:bg-neutral-300/70 hover:bg-neutral-300/70 hover:ring hover:ring-neutral-200 ':
          props.severity === ButtonSeverity.ghost,
      },

      { 'bg-negative': props.severity === ButtonSeverity.danger },
      { 'hover:bg-negative': props.severity === ButtonSeverity.danger },

      { 'bg-error': props.severity === ButtonSeverity.error },

      { 'bg-orange-400': props.severity === ButtonSeverity.warning },
      'focus:outline-none focus:outline-offset-0 focus:ring',

      // Link
      {
        'focus:ring-primary-400/50 dark:focus:ring-primary-300/50': props.link,
      },

      // Plain
      {
        'hover:bg-gray-600 hover:border-gray-600': props.plain && !props.outlined && !props.text,
      },
      // Text & Outlined Button
      {
        'hover:bg-surface-300/20': props.plain && (props.text || props.outlined),
      },
      // Danger

      {
        'focus:ring-red-400/50 dark:focus:ring-red-300/50 hover:ring hover:ring-red-400/70':
          props.severity === ButtonSeverity.danger,
      },
      // Primary
      {
        'hover:bg-primary-600 dark:hover:bg-primary-300 hover:border-primary-600 dark:hover:border-primary-300':
          !props.link && props.severity === null && !props.text && !props.outlined && !props.plain,
      },
      {
        'focus:ring-primary-400/50 dark:focus:ring-primary-300/50': props.severity === null,
      },
      // Text & Outlined Button
      {
        'hover:bg-primary-300/20': (props.text || props.outlined) && props.severity === null && !props.plain,
      },

      // Secondary
      {
        'hover:bg-surface-600 dark:hover:bg-surface-300 hover:border-surface-600 dark:hover:border-surface-300':
          props.severity === ButtonSeverity.secondary && !props.text && !props.outlined && !props.plain,
      },
      {
        'focus:ring-surface-400/50 dark:focus:ring-surface-300/50': props.severity === ButtonSeverity.secondary,
      },
      // Text & Outlined Button
      {
        'hover:bg-surface-300/20':
          (props.text || props.outlined) && props.severity === ButtonSeverity.secondary && !props.plain,
      },

      // Success
      {
        'hover:bg-green-600 dark:hover:bg-green-300 hover:border-green-600 dark:hover:border-green-300':
          props.severity === ButtonSeverity.success && !props.text && !props.outlined && !props.plain,
      },
      {
        'focus:ring-green-400/50 dark:focus:ring-green-300/50': props.severity === ButtonSeverity.success,
      },

      //download
      { 'bg-blue-500': props.severity === ButtonSeverity.download },

      {
        'focus:ring-blue-400/70 focus:bg-blue-400/70 hover:ring-blue-400/70 hover:ring':
          props.severity === ButtonSeverity.download,
      },
      // Text & Outlined Button
      {
        'hover:bg-green-300/20':
          (props.text || props.outlined) && props.severity === ButtonSeverity.success && !props.plain,
      },

      // Info
      {
        'hover:bg-blue-600 dark:hover:bg-blue-300 hover:border-blue-600 dark:hover:border-blue-300':
          props.severity === ButtonSeverity.info && !props.text && !props.outlined && !props.plain,
      },
      {
        'focus:ring-blue-400/50 dark:focus:ring-blue-300/50': props.severity === ButtonSeverity.info,
      },
      // Text & Outlined Button
      {
        'hover:bg-blue-300/20':
          (props.text || props.outlined) && props.severity === ButtonSeverity.info && !props.plain,
      },

      // Warning
      {
        'hover:bg-orange-600 dark:hover:bg-orange-300 hover:border-orange-600 dark:hover:border-orange-300':
          props.severity === ButtonSeverity.warning && !props.text && !props.outlined && !props.plain,
      },
      {
        'focus:ring-orange-400/50 dark:focus:ring-orange-300/50': props.severity === ButtonSeverity.warning,
      },
      // Text & Outlined Button
      {
        'hover:bg-orange-300/20':
          (props.text || props.outlined) && props.severity === ButtonSeverity.warning && !props.plain,
      },

      {
        'bg-secondaryBrown/90 hover:bg-secondaryBrown dark:hover:bg-error-300 hover:border-secondaryBrown dark:hover:border-error-300':
          props.severity === ButtonSeverity.tertiary && !props.text && !props.outlined && !props.plain,
      },
      {
        'focus:ring-secondaryBrown/50 dark:focus:ring-secondaryBrown/50': props.severity === ButtonSeverity.tertiary,
      },

      // Help
      {
        'hover:bg-purple-600 dark:hover:bg-purple-300 hover:border-purple-600 dark:hover:border-purple-300':
          props.severity === ButtonSeverity.help && !props.text && !props.outlined && !props.plain,
      },
      {
        'focus:ring-purple-400/50 dark:focus:ring-purple-300/50': props.severity === ButtonSeverity.help,
      },
      // Text & Outlined Button
      {
        'hover:bg-purple-300/20':
          (props.text || props.outlined) && props.severity === ButtonSeverity.help && !props.plain,
      },

      // Text & Outlined Button
      {
        'hover:bg-red-300/20 ':
          (props.text || props.outlined) && props.severity === ButtonSeverity.danger && !props.plain,
      },

      // Disabled
      { 'opacity-60 pointer-events-none cursor-default': context.disabled },
    ],
  }),
  label: 'text-white font-bold text-xl', // OR { class: 'text-white font-bold text-xl' }
  icon: 'text-white text-2xl',
};
